import { Util } from "../utils/util";
import {
    IAddDiscountToOrder, IBatchRedemptionProcess, IFetchGiftCardsBalance, IFetchGiftCards, IGetActiveDiscountBasket,
    IGetStoreLoyaltyConfigurationInfo, ILoyaltyServiceGenericResponse, IRemoveDiscountFromOrder,
    ICreateFinishOrderPayloadLoyaltyResult, IPostOrderResponse,
    GetPointsRequest
} from "./loyalty-service.interface";
import { Data } from "../models/data";
import { ISignIn, ISignInSSO, ISignInPhone, ISignInResponse } from "./profile-service.interface";
import { HttpService } from "./http-service";
import { IService } from "./service.interface";

export class LoyaltyService implements IService {
    private readonly _http: HttpService;
    private readonly _data: Data;
    private readonly _apiLoyalty: string;
    private readonly _publicKey: string;

    constructor(http: HttpService, data: Data, apiLoyalty: string, publicKey: string) {
        this._http = http;
        this._data = data;
        this._apiLoyalty = apiLoyalty;
        this._publicKey = publicKey;
    }

    public httpRequestAsync(method: string, url: string, payload: object | null = null, options?: { showProgress?: boolean, noCache?: boolean }): Promise<Response> {
        const storeKey = this._data.getProperty("StoreInfo")?.storeKey
            ?? this._data.getBrandProperty("StoreInfo").storeKey;

        const timeOffset = this._data.getProperty("TimeOffset")
            ?? this._data.getBrandProperty("TimeOffset");

        const businessStartMinute = this._data.getProperty("Settings")?.BSM
            ?? this._data.getBrandProperty("Settings").BSM;

        let businessDate = new Date().toISOString();
        try {
            businessDate = Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute).toISOString();
        } catch { }

        return this._http.request(method, url, payload, storeKey, businessDate, { showProgress: options?.showProgress, noCache: options?.noCache });
    }

    /*********************************
     ******* Loyalty API Calls *******
     *********************************/

    /**
     * Registers a new user account.
     * @async
     * @param cp A complex object representing the user's profile and contact information.
     * @returns {Promise<number | null>} The response data from the server, or null if the request failed.
     */
    public signUp = async (cp: any): Promise<number | null> => {
        const payload = await Util.encrypt(JSON.stringify({ storeKey: this._data.getBrandProperty("StoreInfo").storeKey, location: "olo", profile: cp }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/sign_up`, payload);
        if (response.status !== 200) {
            //DialogCreators.messageBoxOk(await AdoraHttpClient.getResponsePayload(response));//DialogCreators.messageBoxOk(await AdoraHttpClient.getResponsePayload(response));
            return null;
        }

        const data = await this._http.getJson(response);
        if (data.success)
            return data.data;

        return null;
    }

    /**
    * Attempts to log in the user with the given credentials.
    * @async
    * @param brandId The ID of the brand to log in to.
    * @param email The user's email address.
    * @param password The user's password.
    * @param culture The user's preferred language/culture.
    * @param rememberMe The user's selection of the "Remember Me" option.
    * @returns {Promise<ISignInResponse>} 
    *          An object with a `success` property indicating whether the login was successful, and optionally a `user` property containing the user's data if the login was successful.
    */
    public signIn = async (data: ISignIn): Promise<ISignInResponse> => {
        const payload = await Util.encrypt(JSON.stringify({
            storeKey: this._data.getBrandProperty("StoreInfo").storeKey,
            profile: { EML: data.email, pwd: data.password },
            is_app: data?.app,
            remember_me: data?.rememberMe,
            incomplete_profile: data?.incompleteProfile,
            location: "olo"
        }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/sign_in`, payload);

        if (response.status == 200) {
            const result = await this._http.getJson(response);
            if (result?.success) {
                const data = JSON.parse(result.data);
                const user = data.User;
                const jwt = data.JWT;
                const token = data.Token ?? null;
                const refreshToken = data.RefreshToken;

                return { success: true, user: user, jwt: jwt, refreshToken: refreshToken, token: token };
            }
            return { success: false }
        }
        return { success: false, timeout: response.status === 408 };
    }

    public signInJWT = async (): Promise<ISignInResponse> => {
        const encryptedPayload = await Util.encrypt(JSON.stringify({ store_key: this._data.getBrandProperty("StoreInfo").storeKey }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/get_user`, encryptedPayload);

        if (response.status == 200) {
            const result = await this._http.getJson(response);
            if (result?.success) {
                const data = JSON.parse(result.data);
                const user = data.profile;
                const jwt = data.jwt;
                return { success: true, user: user, jwt: jwt }
            }
            return { success: false };
        }
        return { success: false, timeout: response.status === 408 };
    }

    public signInSSO = async (data: ISignInSSO): Promise<ISignInResponse> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/sign_in_sso`, payload);

        if (response.status == 200) {
            const result = await this._http.getJson(response);
            if (result?.success) {
                const data = JSON.parse(result.data);
                const user = data.User;
                const jwt = data.JWT;
                const token = data.Token ?? null;
                const refreshToken = data.RefreshToken;
                return { success: true, user: user, jwt: jwt, refreshToken: refreshToken, token: token };
            }
            return { success: false }
        }
        return { success: false, timeout: response.status === 408 };
    }

    public signInApp = async (appToken: string): Promise<ISignInResponse> => {
        const payload = await Util.encrypt(JSON.stringify({ storeKey: this._data.getBrandProperty("StoreInfo").storeKey, appToken: appToken }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/sign_in_by_app`, payload);
        if (response.status == 200) {
            const result = await this._http.getJson(response);
            if (result?.success) {
                const data = JSON.parse(result.data);
                const user = data.User;
                const jwt = data.JWT;
                const token = data.Token;
                return { success: true, user: user, jwt: jwt, token: token };
            }
            return { success: false }
        }
        return { success: false, timeout: response.status === 408 };
    }

    public signInPhone = async (data: ISignInPhone): Promise<ISignInResponse> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/customer/sign_in_by_phone`, payload);
        if (response.status == 200) {
            const result = await this._http.getJson(response);
            if (result?.success) {
                const data = JSON.parse(result.data);
                const user = data.User;
                const jwt = data.JWT;
                const token = data.Token ?? null;
                const refreshToken = data.RefreshToken;
                return { success: true, user: user, jwt: jwt, refreshToken: refreshToken, token: token, timeout: false };
            }
            return { success: false }
        }
        return { success: false, timeout: response.status === 408 };
    }

    public async generateSsoTokenAsync(): Promise<string | null> {
        const encryptedPayload = await Util.encrypt(JSON.stringify({ store_key: this._data.getBrandProperty("StoreInfo").storeKey }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/customer/create_sso_token`, encryptedPayload);

        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json?.token ?? null;
    }

    public async addDiscountToOrder(payload: IAddDiscountToOrder): Promise<ILoyaltyServiceGenericResponse> {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/discount/add_selection_to_basket`, encryptedPayload);

        if (response.status !== 200)
            return { success: false, data: null, message: await this._http.getJson(response) ?? "", timeout: response.status === 408 };

        const data = await this._http.getJson(response);
        return { success: data.success, data: data.data, message: data.message, timeout: false };
    }

    public async removeDiscountFromOrder(payload: IRemoveDiscountFromOrder): Promise<ILoyaltyServiceGenericResponse> {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/discount/remove_item_from_basket`, encryptedPayload);

        if (response.status !== 200)
            return { success: false, data: null, message: await this._http.getJson(response), timeout: response.status === 408 };

        const data = await this._http.getJson(response);
        return { success: data.success, data: data.data, message: data.message, timeout: false };
    }

    public async getActiveDiscountBasket(payload: IGetActiveDiscountBasket): Promise<ILoyaltyServiceGenericResponse> {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/discount/get_active_basket`, encryptedPayload);

        if (response.status !== 200)
            return { success: false, data: null, message: await this._http.getJson(response), timeout: response.status === 408 };

        const data = await this._http.getJson(response);
        return { success: data.success, data: data.data, message: data.message, timeout: false };
    }

    public async fetchGiftCards(payload: IFetchGiftCards): Promise<ILoyaltyServiceGenericResponse> {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/fetch_gift_cards`, encryptedPayload);
        if (response.status !== 200)
            return { success: false, data: null, message: await this._http.getJson(response), timeout: response.status === 408 };

        const data = await this._http.getJson(response);
        return { success: data.success, data: data.data, message: data.message, timeout: false };
    }

    public async fetchGiftCardsBalance(payload: IFetchGiftCardsBalance): Promise<ILoyaltyServiceGenericResponse> {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/fetch_gift_cards_balance`, encryptedPayload);
        if (response.status !== 200)
            return { success: false, data: null, message: await this._http.getJson(response), timeout: response.status === 408 };

        const data = await this._http.getJson(response);
        return { success: data.success, data: data.data, message: data.message, timeout: false };
    }

    public async postOrderAsync(payload: ICreateFinishOrderPayloadLoyaltyResult): Promise<IPostOrderResponse> {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/post_order`, encryptedPayload);
        if (response.status !== 200)
            return { success: false, message: await this._http.getJson(response) };

        const data = await this._http.getJson(response);
        return { success: data.success, message: data.message };
    }

    public async getPointsAsync(payload: GetPointsRequest): Promise<number> {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/points`, encryptedPayload);

        const points = response.status === 200 ? await this._http.getJson(response) : 0;
        return points;
    }

    /****************************
     ******* Punchh Calls *******
     ****************************/

    public getStoreLoyaltyConfigurationInfo = async (payload: IGetStoreLoyaltyConfigurationInfo): Promise<ILoyaltyServiceGenericResponse> => {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/configuration`, encryptedPayload);
        if (response.status == 401)
            return { success: false, data: null, message: "Unauthorized", timeout: false };

        if (response.status !== 200)
            return { success: false, data: null, message: await this._http.getJson(response), timeout: response.status === 408 };

        const data = await this._http.getJson(response);
        return { success: data.success, data: data.data, message: null, timeout: false };
    }

    public async batchRedemptionProcess(payload: IBatchRedemptionProcess): Promise<ILoyaltyServiceGenericResponse> {
        const encryptedPayload = await Util.encrypt(JSON.stringify(payload), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/discount/batch_redemption`, encryptedPayload);
        if (response.status !== 200)
            return { success: false, data: null, message: await this._http.getJson(response), timeout: response.status === 408 };

        const data = await this._http.getJson(response);
        return { success: data.success, data: data.data, message: data.message, timeout: false };
    }
}